type ProgramEnrollment = {
  id: string;
  updatedAt?: string;
  status?: string;
  program: { id: string };
};

type CurrentEnrollmentMetadata = {
  status: string;
  programId: string;
};

export const selectCurrentDashboardResumables = <T extends ProgramEnrollment>(
  resumableEnrollments: readonly T[],
  currentEnrollments: readonly CurrentEnrollmentMetadata[]
) => {
  const submittedProgramIds = new Set(
    currentEnrollments
      .filter((enrollment) => enrollment.status === "LEAD")
      .map((enrollment) => enrollment.programId)
  );

  return resumableEnrollments.filter(
    (enrollment) => !submittedProgramIds.has(enrollment.program.id)
  );
};

const preferLatestByProgram = <T extends ProgramEnrollment>(items: readonly T[]): T[] => {
  const byProgram = new Map<string, T>();

  items.forEach((item) => {
    const existing = byProgram.get(item.program.id);
    if (!existing || (item.updatedAt ?? "") > (existing.updatedAt ?? "")) {
      byProgram.set(item.program.id, item);
    }
  });

  return Array.from(byProgram.values());
};

export const buildDashboardProgramSections = <
  TEnrollment extends ProgramEnrollment,
  TFavorite extends ProgramEnrollment,
  TResumable extends ProgramEnrollment,
>({
  enrollments,
  resumableEnrollments,
  favorites,
}: {
  enrollments: readonly TEnrollment[];
  resumableEnrollments: readonly TResumable[];
  favorites: readonly TFavorite[];
}) => {
  const submitted = preferLatestByProgram(
    enrollments.filter((enrollment) => enrollment.status === "LEAD")
  );
  const resumable = preferLatestByProgram(resumableEnrollments).sort((a, b) =>
    (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "")
  );
  const displayedProgramIds = new Set([
    ...submitted.map((enrollment) => enrollment.program.id),
    ...resumable.map((enrollment) => enrollment.program.id),
  ]);
  const saved = preferLatestByProgram(favorites).filter(
    (favorite) => !displayedProgramIds.has(favorite.program.id)
  );

  return { resumable, submitted, saved };
};
